/**
 * ProtectionTab.tsx
 *
 * WAF "Protection" inner tab — DLP, GraphQL security, L7 DDoS mitigation,
 * and composite threat scoring. Receives AiCfg state + setter via props;
 * save/cancel is handled by the outer WafTab shell.
 */
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { SettingRow } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, IconButton, Input, Switch, SegmentedControl,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { AiCfg } from './waf-types';

const DDOS_LEVELS = ['off', 'low', 'medium', 'high'];

export function ProtectionTab({ ai, setAi }: { ai: AiCfg; setAi: <K extends keyof AiCfg>(k: K, v: AiCfg[K]) => void }) {
  const [epDraft, setEpDraft] = useState('');
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <p style={{ fontSize: 'var(--vn-font-size-md)', color: 'var(--vn-fg-muted)', margin: 0 }}>Advanced protection engines: DLP, GraphQL security, L7 DDoS mitigation, and composite threat scoring.</p>

      {/* DLP */}
      <Card>
        <CardHeader><CardTitle>Data Leak Prevention (DLP)</CardTitle><CardDescription>Scans outbound responses for sensitive data patterns.</CardDescription></CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <SettingRow
            title="Mode"
            control={
              <SegmentedControl
                size="sm"
                value={ai.dlp_mode || 'off'}
                onValueChange={(v) => setAi('dlp_mode', v === 'off' ? '' : v)}
                items={[{ value: 'off', label: 'Off' }, { value: 'detect', label: 'Detect' }, { value: 'block', label: 'Block' }]}
              />
            }
          />
          <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Patterns detected: Credit cards · SSN · AWS keys · Private keys · Stack traces · SQL errors · Emails</div>
        </CardBody>
      </Card>

      {/* GraphQL */}
      <Card>
        <CardHeader><CardTitle>GraphQL protection</CardTitle><CardDescription>Enforce query-depth limits and block introspection on GraphQL endpoints.</CardDescription></CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Field label="Max query depth">{(p) => <Input {...p} type="number" value={String(ai.graphql_max_depth)} onChange={(e) => setAi('graphql_max_depth', Number(e.target.value) || 0)} />}</Field>
          </div>
          <SettingRow title="Block introspection" control={<Switch checked={ai.graphql_block_introspection} onCheckedChange={(v) => setAi('graphql_block_introspection', v)} />} />
          <Field label="Endpoints">
            {() => (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {ai.graphql_endpoints.map((ep) => (
                  <div key={ep} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                    <code style={{ flex: 1, fontSize: 'var(--vn-font-size-sm)', fontFamily: 'var(--vn-font-mono)' }}>{ep}</code>
                    <IconButton variant="ghost" size="sm" aria-label={`Remove ${ep}`} onClick={() => setAi('graphql_endpoints', ai.graphql_endpoints.filter((x) => x !== ep))}><Trash2 size={14} /></IconButton>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
                  <Input placeholder="/graphql" value={epDraft} onChange={(e) => setEpDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (epDraft.trim()) { setAi('graphql_endpoints', [...ai.graphql_endpoints, epDraft.trim()]); setEpDraft(''); } } }} />
                  <Button variant="secondary" startIcon={<Plus size={14} />} onClick={() => { if (epDraft.trim()) { setAi('graphql_endpoints', [...ai.graphql_endpoints, epDraft.trim()]); setEpDraft(''); } }}>Add</Button>
                </div>
              </div>
            )}
          </Field>
        </CardBody>
      </Card>

      {/* DDoS L7 */}
      <Card>
        <CardHeader><CardTitle>DDoS L7 protection</CardTitle><CardDescription>Behavioral detection of application-layer DDoS attacks.</CardDescription></CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <SettingRow
            title="Sensitivity"
            control={
              <SegmentedControl
                size="sm"
                value={DDOS_LEVELS[ai.ddos_sensitivity] ?? 'off'}
                onValueChange={(v) => setAi('ddos_sensitivity', Math.max(0, DDOS_LEVELS.indexOf(v)))}
                items={DDOS_LEVELS.map((l) => ({ value: l, label: l[0].toUpperCase() + l.slice(1) }))}
              />
            }
          />
          <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Detects: Rate anomaly · Method abuse · Tool user-agents</div>
        </CardBody>
      </Card>

      {/* Threat scoring */}
      <Card>
        <CardHeader><CardTitle>Threat scoring</CardTitle><CardDescription>Composite threat score combining multiple signals per request.</CardDescription></CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Field label="Threshold (0–100)">{(p) => <Input {...p} type="number" value={String(ai.threat_threshold)} onChange={(e) => setAi('threat_threshold', Number(e.target.value) || 0)} />}</Field>
            <Field label="Body scan limit (KB)" description="0 = disabled, max 10 MB">{(p) => <Input {...p} type="number" value={String(ai.body_scan_limit)} onChange={(e) => setAi('body_scan_limit', Number(e.target.value) || 0)} />}</Field>
            <Field label="Block oversized body">
              {(p) => (
                <Select value={ai.block_oversized_body} onValueChange={(v) => setAi('block_oversized_body', v)}>
                  <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="auto">Auto</SelectItem><SelectItem value="on">Always</SelectItem><SelectItem value="off">Never</SelectItem></SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <div style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Signals: IP reputation · DDoS · API abuse · WAF</div>
        </CardBody>
      </Card>
    </div>
  );
}
